import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { getUser, getIntercomToken } from "~/api/account/requests";
import { AccountContext } from "~/contexts/Account";
import { IAccount } from "~/types/account";
import _debounce from "lodash/debounce";
import { useIntercom } from "react-use-intercom";
import { intercomKey } from "~/helpers/environment";
import { DISABLE_PAYMENTS } from "~/helpers/plans";
import { useStore } from "~/hooks/useStore";
import { log } from "~/helpers/datadog";
import { getHasJWT, getHasRefreshToken } from "~/helpers/auth";
import { isReportableRequestError } from "~/helpers/errors";
import { isGuestPath } from "~/constants/paths";

export const useAccount = () => {
  const { account, setAccount } = useContext(AccountContext);
  const [isLoading, setIsLoading] = useState(false);
  const { boot } = useIntercom();
  const { store } = useStore();
  const intercomBooted = useRef(false);
  const APP_IDENTIFIER = "dropshiptool-app";

  const bootIntercomForAccount = async (account: IAccount) => {
    if (!intercomKey) {
      return;
    }

    let intercom_user_jwt: string;
    try {
      ({ intercom_user_jwt } = await getIntercomToken());
    } catch (error) {
      log.error("Failed to fetch Intercom token", error);
      return;
    }

    boot({
      email: account.email,
      userId: `dropshiptool_${account.id.toString()}`,
      name: account.name,
      intercomUserJwt: intercom_user_jwt,
      customAttributes: {
        app: APP_IDENTIFIER,
        shop_name: store?.name,
        shop_url: store?.url,
        shop_email: store?.email,
        shop_phone: store?.phone,
      },
    });
  };

  const bootIntercom = () => {
    if (!account || intercomBooted.current) return;
    intercomBooted.current = true;
    bootIntercomForAccount(account);
  };

  const setAccountWithThirdParties = (acc: IAccount | null) => {
    setAccount(acc);
    if (acc) {
      // Microsoft Clarity - attach the user's email to the recorded session.
      // The friendly-name (5th arg) is stored unmasked; the custom-id is hashed by Clarity.
      window.clarity?.("identify", acc.email, null, null, acc.email);
      window.clarity?.("set", "user_email", acc.email);
      if (
        (acc.active_subscription || DISABLE_PAYMENTS) &&
        !intercomBooted.current
      ) {
        intercomBooted.current = true;
        bootIntercomForAccount(acc);
      }
    }
  };

  const getAccount = async () => {
    setIsLoading(true);
    try {
      const profile = (await getUser()) as IAccount;
      setAccountWithThirdParties(profile);
      return profile;
    } finally {
      setIsLoading(false);
    }
  };
  // The catch() ensures failures during the silent background fetch don't
  // surface as unhandled promise rejections (e.g. when the debounce timer
  // fires after the component - or a test's jsdom environment - is gone).
  const debouncedGetAccount = useCallback(
    _debounce(
      () =>
        getAccount().catch((error) => {
          // A 401 or an ended session is handled by the sign-out that raised
          // it; only a server fault or an unreachable API is worth a log line.
          if (isReportableRequestError(error)) {
            log.error("Failed to fetch account", error);
          }
        }),
      200
    ),
    []
  );

  useEffect(() => {
    // The login, register and forgot-password hooks mount this too, only to
    // get setAccount. With no session on a guest page there is no account to
    // fetch: the request could only come back 401 (34k logged a week).
    const hasSession = getHasJWT() || getHasRefreshToken();
    if (!account && (hasSession || !isGuestPath(window.location.pathname))) {
      setIsLoading(true);
      debouncedGetAccount();
    }
    // Cancel the pending debounce on unmount so it can never fire against an
    // unmounted tree.
    return () => debouncedGetAccount.cancel();
  }, []);

  return {
    account,
    setAccount: setAccountWithThirdParties,
    getAccount,
    refetch: getAccount,
    bootIntercom,
    isLoading,
  };
};
